import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Info } from 'lucide-react';
import { HeaderProps } from '../../types/Header.types';
// import UserAvatar from '../molecules/UserAvatar';
import { HeaderMenu } from './HeaderMenu';
import { AuthButtons } from '../molecules/AuthButtons';
// import Logo from '../atoms/Logo';
import { Button } from '../atoms/button';
import { AnimatedBanner } from './AnimatedBanner';
import { cn } from '@ideoz/ui';

/**
 * Header organism component matching LandingHeader structure.
 *
 * **Left Side**:
 * - Default: Logo component
 * - With back button: ArrowLeft icon + backText + optional HeaderMenu
 *
 * **Right Side**:
 * - Always: PointsDisplay
 * - Authenticated: UserAvatar
 * - Unauthenticated: AuthButtons
 *
 * @example
 * // Default header
 * <Header
 *   user={{ name: 'John Doe' }}
 *   points={1250}
 *   pointsLabel="Points"
 *   menuItems={[...]}
 *   onUserClick={handleProfile}
 * />
 *
 * @example
 * // With back button and menu
 * <Header
 *   showBackButton
 *   backText="Project Details"
 *   backRoute="/projects"
 *   isHaderMenu
 *   user={user}
 *   points={points}
 *   menuItems={[...]}
 * />
 */
export const Header: React.FC<HeaderProps> = ({
  showBackButton = false,
  showBackArrow = true,
  backText = 'Back',
  onBack,
  isHaderMenu = false,
  isHasBorder = false,
  logoProps,
  user,
  // points = 0,
  // pointsLabel = 'points remaining',
  menuItems = [],
  onUserClick,
  loginLabel = 'Login',
  registerLabel = 'Sign Up',
  onLoginClick,
  onRegisterClick,
  onLoginAnalytics,
  onRegisterAnalytics,
  className = '',
  testId = 'header',
  isWeb = true,
  hideHeaderContent = false,
  creditLimitBanner,
  rightActions,
}) => {
  const { i18n } = useTranslation();
  const BackArrow = i18n.dir() === 'rtl' ? ArrowRight : ArrowLeft;
  const isAuthenticated = !!user;
  // const wallets = user?.wallets || [];

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    }
    // Platform code should handle backRoute navigation
  };

  return (
    <div className={hideHeaderContent && creditLimitBanner?.isVisible ? 'h-[60px]' : ''}>
      <header
        id={testId}
        className={cn(
          `sticky top-0 z-[35] border-b border-gray-200 dark:border-gray-800`,
          className
        )}
        data-testid={testId}
      >
        {!hideHeaderContent && (
          <div className="flex justify-between items-center py-4 px-6 z-[35] h-16">
            <div className="flex">
              {showBackButton ? (
                <div className="flex items-center gap-2">
                  {showBackArrow && (
                    <Button
                      variant="ghost"
                      className="flex justify-center items-center rounded-full hover:bg-gray-200 dark:hover:bg-gray-800 w-8 h-8 text-gray-700 dark:text-gray-200"
                      size="auto"
                      onClick={handleBackClick}
                      data-testid="btn-back"
                      data-track="back_button"
                      data-track-from-page={backText}
                    >
                      <BackArrow size={18} />
                    </Button>
                  )}
                  <span
                    data-testid="chat-thread-title"
                    className="max-w-2xl overflow-hidden text-ellipsis text-gray-800 dark:text-gray-100"
                  >
                    {backText}
                  </span>
                  {isHaderMenu && menuItems.length > 0 && (
                    <HeaderMenu menuItems={menuItems} testId={`${testId}-menu`} />
                  )}
                </div>
              ) : (
                // <div className="flex">
                //   <Logo {...logoProps} />
                // </div>
                <></>
              )}
            </div>

            <div className="flex items-center gap-2">
              {rightActions}

              {isAuthenticated ? (
                <></>
              ) : (
                <AuthButtons
                  loginLabel={loginLabel}
                  registerLabel={registerLabel}
                  onLogin={onLoginClick || (() => {})}
                  onRegister={onRegisterClick || (() => {})}
                  onLoginAnalytics={onLoginAnalytics}
                  onRegisterAnalytics={onRegisterAnalytics}
                />
              )}
            </div>
          </div>
        )}
      </header>
      {creditLimitBanner?.isVisible && (
        <AnimatedBanner
          className={hideHeaderContent ? 'top-[10px]' : ''}
          isVisible={creditLimitBanner.isVisible}
          icon={Info}
          text={
            creditLimitBanner.totalCredits > 0
              ? `${creditLimitBanner.planName} plan is eligible for ${creditLimitBanner.totalCredits} credits per month.`
              : creditLimitBanner.bannerText
          }
          subText={creditLimitBanner.totalCredits > 0 ? creditLimitBanner.bannerText : ''}
          actionText={creditLimitBanner.isFreePlan ? 'Upgrade' : 'Learn more'}
          onAction={creditLimitBanner.onAction}
          onClose={creditLimitBanner.onDismiss}
          showCloseButton={true}
          bgColor="bg-blue-light-50"
          variant={isWeb ? 'web' : 'plugin'}
          testId="credit-limit-banner"
        />
      )}
    </div>
  );
};
